<template>
  <div class="submissions-table">
    <div class="bulk" :class="{ 'bulk-hidden': !selected.length }">
      <v-btn style="margin-right: 10px" color="primary" @click="publish"
        >Bulk Publish</v-btn
      >
      <v-btn color="primary" @click="unpublish" outlined>Bulk Unpublish</v-btn>
    </div>
    <div v-if="list">
      <div v-if="list.length">
        <v-data-table
          :headers="headers"
          :items="list"
          item-key="id"
          show-expand
          class="elevation-0 data-table"
          disable-pagination
          :items-per-page="-1"
          :hide-default-footer="true"
          show-select
          v-model="selected"
          v-if="entity && entity.manual_order"
        >
          <template v-slot:body="{ items }">
            <draggable v-model="list" tag="tbody" @end="dragReorder()">
              <SubmissionsDataRow
                v-for="(item, idx) in items"
                :key="item.id"
                :value="item"
                :headers="headers"
                @select="manualOrderSelect($event, idx)"
                :isSelected="manualOrderIsSelected(idx)"
                @moveUp="moveUp(item.id)"
                @moveDown="moveDown(item.id)"
                :submissionForm="entity"
                @destroy="destroy(item)"
                manualOrder
              />
            </draggable>
          </template>
        </v-data-table>

        <v-data-table
          :headers="headers"
          :items="list"
          item-key="id"
          show-expand
          class="elevation-0 data-table"
          disable-pagination
          :items-per-page="-1"
          :hide-default-footer="true"
          show-select
          v-model="selected"
          v-else
        >
          <template v-slot:item="{ item, isSelected, select }">
            <SubmissionsDataRow
              :value="item"
              :headers="headers"
              @select="select($event)"
              :isSelected="isSelected"
              @moveUp="moveUp(item.id)"
              @moveDown="moveDown(item.id)"
              :submissionForm="entity"
              @destroy="destroy(item)"
            />
          </template>
        </v-data-table>
      </div>
      <div v-else class="no-entries">No Submissions</div>
    </div>
    <Spinner v-else />
  </div>
</template>

<script>
import _ from "lodash";
import draggable from "vuedraggable";
import SubmissionsDataRow from "./submissions.datarow";

export default {
  components: {
    draggable,
    SubmissionsDataRow,
  },
  props: {
    entity: Object,
  },
  data() {
    return {
      list: null,
      selected: [],
    };
  },
  created() {
    this.setList();
  },
  methods: {
    async publish() {
      await this.$api.Submission.bulkPublish({
        submission_ids: this.selectedIds,
      });
      this.setList();
    },
    async unpublish() {
      await this.$api.Submission.bulkUnpublish({
        submission_ids: this.selectedIds,
      });
      this.setList();
    },
    async destroy(submission) {
      const confirmed = await this.$confirmWindow({ title: "Are you sure?" });
      if (!confirmed) return;
      await this.$api.Submission.destroy({ id: submission.id });

      this.list = this.list.filter((t) => t.id != submission.id);
    },
    async setList() {
      const { data } = await this.$api.Submission.lite({
        submission_form_id: this.entity.id,
      });
      this.list = data.map((t) => ({
        ...t,
        indexed_formatted_data: _.keyBy(t.formatted_data, "uuid"),
      }));
    },
    async moveUp(id) {
      const res = await this.$api.Submission.moveUp({
        id,
      });
      if (res.data.msg) {
        this.$allertWindow(res.data.msg, "error");
      }
      this.setList();
    },
    async moveDown(id) {
      const res = await this.$api.Submission.moveDown({
        id,
      });
      if (res.data.msg) {
        this.$allertWindow(res.data.msg, "error");
      }
      this.setList();
    },
    manualOrderSelect(event, idx) {
      if (event) this.selected.push(this.list[idx]);
      else {
        const indexOfItem = this.selected.indexOf(this.list[idx]);
        this.selected.splice(indexOfItem, 1);
      }
    },
    manualOrderIsSelected(idx) {
      return this.selected.indexOf(this.list[idx]) != -1;
    },
    async dragReorder() {
      this.list.forEach((item, idx) => {
        item.position = idx;
      });

      const res = await this.$api.Submission.reorder({
        list: this.list.map((item) => item.id),
      });
      if (res.data.msg) {
        this.$allertWindow(res.data.msg, "error");
      }
      this.setList();
    },
  },
  computed: {
    selectedIds() {
      return this.selected.map((t) => t.id);
    },
    headers() {
      let headers = (this.list || []).map((t) => {
        return t.formatted_data.map((t) => {
          const val = t.short_name || t.label || "";
          const text = val.substr(0, 15);
          return {
            text: text.length < val.length ? `${text}...` : text,
            align: "start",
            uuid: t.uuid,
            type: t.type,
            generic: true,
            sortable: !this.entity.manual_order,
          };
        });
      });

      headers = _.sortBy(_.uniqBy(_.flatten(headers), "uuid"), "short_name");

      const allHeaders = [
        {
          text: "Submitted date",
          align: "left",
          value: "created_at",
          type: "created_at",
          sortable: !this.entity.manual_order,
        },
        {
          text: "Status",
          align: "left",
          value: "published",
          type: "status",
          sortable: !this.entity.manual_order,
        },
        {
          text: "User",
          align: "left",
          value: "user_name",
          type: "text",
        },
        {
          text: "Email",
          align: "left",
          value: "email",
          type: "text",
        },
        ...headers,
        {
          text: "Actions",
          align: "center",
          type: "actions",
          sortable: false,
        },
      ];

      if (this.entity.manual_order) {
        allHeaders.unshift({
          text: "Order",
          align: "left",
          value: "position",
          type: "order",
        });
      }

      return allHeaders.filter(
        (h) => !["MediaField", "DateTime"].includes(h.type)
      );
    },
  },
};
</script>

<style scoped lang="scss">
.submissions-table {
  &::v-deep th:first-child {
    display: none !important;
  }
}

.bulk-hidden {
  pointer-events: none;
  opacity: 0;
}

.bulk {
  margin-top: -30px;
}
</style>
